import { trackPublicWebEvent } from "@/shared/analytics/analytics";
import { readAccountScopedStorage, writeAccountScopedStorage } from "@/shared/storage/account-scope";
import { stringValue } from "./audio-media-utils.mjs";
import { CURRENT_WEB_AUDIO_QUEUE_CONTRACT, normalizeAudioQueueEntry, queuedAudioEntryIsCurrentMarketDay } from "./audio-queue-policy";
import type { AudioHistoryEntry, AudioQueueEntry } from "./audio-types";
import type { AudioPlaybackState } from "./audio-playback-state";

const libraryEvents = new EventTarget();
const AUDIO_HISTORY_KEY = "tb_public_web_audio_history_v1";
const AUDIO_QUEUE_KEY = "tb_public_web_audio_queue_v1";
const AUDIO_HISTORY_LIMIT = 20;
const AUDIO_QUEUE_LIMIT = 12;

function notifyAudioLibraryChanged() {
  libraryEvents.dispatchEvent(new Event("change"));
}
export function onAudioLibraryChanged(listener: () => void): () => void {
  libraryEvents.addEventListener("change", listener);
  return () => libraryEvents.removeEventListener("change", listener);
}

function readJsonArray<T>(key: string, normalize: (value: unknown) => T | null): T[] {
  try {
    const raw = readAccountScopedStorage(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return (Array.isArray(parsed) ? parsed : [])
      .map(normalize)
      .filter((entry): entry is T => Boolean(entry));
  } catch {
    return [];
  }
}

function writeJsonArray<T>(key: string, entries: T[]) {
  try {
    writeAccountScopedStorage(key, JSON.stringify(entries));
    notifyAudioLibraryChanged();
  } catch {
    /* audio library writes are best-effort */
  }
}

function audioEntryId(prefix: string): string {
  try {
    return `${prefix}-${crypto.randomUUID()}`;
  } catch {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function normalizeHistoryEntry(value: unknown): AudioHistoryEntry | null {
  const entry = value && typeof value === "object" ? (value as Partial<AudioHistoryEntry>) : null;
  const playedAtEpochMs = Number(entry?.playedAtEpochMs) || 0;
  const key = stringValue(entry?.key);
  const label = stringValue(entry?.label);
  if (!playedAtEpochMs || !key || !label) return null;
  return {
    id: stringValue(entry?.id) || audioEntryId("history"),
    key,
    label,
    detail: stringValue(entry?.detail) || undefined,
    playedAtEpochMs,
    cache: stringValue(entry?.cache) || undefined,
  };
}

export function normalizeQueueEntry(value: unknown): AudioQueueEntry | null {
  return normalizeAudioQueueEntry(value, () => audioEntryId("queue"));
}
export function getAudioHistory(): AudioHistoryEntry[] {
  return readJsonArray(AUDIO_HISTORY_KEY, normalizeHistoryEntry)
    .sort((a, b) => b.playedAtEpochMs - a.playedAtEpochMs)
    .slice(0, AUDIO_HISTORY_LIMIT);
}
export function getAudioQueue(): AudioQueueEntry[] {
  const stored = readJsonArray(AUDIO_QUEUE_KEY, normalizeQueueEntry);
  const current = stored.filter((entry) => queuedAudioEntryIsCurrentMarketDay(entry));
  if (current.length !== stored.length) writeJsonArray(AUDIO_QUEUE_KEY, current);
  return current
    .sort((a, b) => a.queuedAtEpochMs - b.queuedAtEpochMs)
    .slice(0, AUDIO_QUEUE_LIMIT);
}

export function recordAudioHistory(input: {
  key: string;
  label: string;
  detail?: string;
  cache?: string;
}) {
  const entry: AudioHistoryEntry = {
    id: audioEntryId("history"),
    key: input.key,
    label: input.label,
    detail: input.detail,
    playedAtEpochMs: Date.now(),
    cache: input.cache,
  };
  const deduped = getAudioHistory().filter((item) => item.key !== input.key);
  writeJsonArray(AUDIO_HISTORY_KEY, [entry, ...deduped].slice(0, AUDIO_HISTORY_LIMIT));
}

export function recordAudioPlaybackStarted(
  state: AudioPlaybackState,
  key = state.key || "audio",
  fallbackLabel = "",
  fallbackDetail?: string,
) {
  recordAudioHistory({
    key: state.key || key,
    label: state.label || fallbackLabel,
    detail: state.detail || fallbackDetail,
    cache: state.cache,
  });
  trackPublicWebEvent("public_web_audio_play_started", {
    audio_kind: key.split(":")[0] || "audio",
    cache: state.cache || "none",
  });
}

export function enqueueAudioRequest(input: {
  key: string;
  label: string;
  detail?: string;
  path: string;
  body: Record<string, unknown>;
  fallbackCode: string;
  downloadName?: string;
}): AudioQueueEntry {
  const entry: AudioQueueEntry = {
    contractVersion: CURRENT_WEB_AUDIO_QUEUE_CONTRACT,
    id: audioEntryId("queue"),
    key: input.key,
    label: input.label,
    detail: input.detail,
    queuedAtEpochMs: Date.now(),
    request: {
      path: input.path,
      body: input.body,
      fallbackCode: input.fallbackCode,
      downloadName: input.downloadName,
    },
  };
  const deduped = getAudioQueue().filter((item) => item.key !== input.key);
  writeJsonArray(AUDIO_QUEUE_KEY, [...deduped, entry].slice(0, AUDIO_QUEUE_LIMIT));
  trackPublicWebEvent("public_web_audio_queued", {
    audio_kind: input.key.split(":")[0] || "audio",
  });
  return entry;
}
export function removeAudioQueueEntry(id: string): AudioQueueEntry[] {
  const next = getAudioQueue().filter((entry) => entry.id !== id);
  writeJsonArray(AUDIO_QUEUE_KEY, next);
  return next;
}
export function clearAudioHistory(): AudioHistoryEntry[] {
  writeJsonArray(AUDIO_HISTORY_KEY, []);
  return [];
}
export function clearAudioQueue(): AudioQueueEntry[] {
  writeJsonArray(AUDIO_QUEUE_KEY, []);
  return [];
}
